import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useMFSurveyFormContext } from '~/microfrontend/hooks/useMFSurveyFormContext';
import { ExtraQuestion } from '~/widgets/survey/components/ExtraQuestion';
import { QuestionRenderer } from '~/widgets/survey/components/QuestionRenderer';
import { SurveyHeaderText } from '~/widgets/survey/components/SurveyHeaderText';
import { useRootStore } from '~base/hooks/useRootStore';
import { DataShower } from '~base/ui/DataShower';
import { SurveyForm, SurveyFormField } from '~modules/surveys/forms/SurveyForm';
import { getSurveyFormValidationSchema } from '~modules/surveys/schema/SurveyFormValidationSchema';

export const SurveyFormWidget: React.FC = observer(() => {
  const { surveyStore } = useRootStore();

  const { carId, workOrderId, onSave } = useMFSurveyFormContext();

  const { t } = useTranslation('survey');

  const methods = useForm<SurveyForm>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: SurveyForm.create(),
    resolver: yupResolver(getSurveyFormValidationSchema()),
  });
  const { handleSubmit, control, formState, reset, trigger } = methods;

  const { fields, append } = useFieldArray({
    name: SurveyFormField.questions,
    control,
  });

  // Effects

  useEffect(() => {
    surveyStore.getSurvey({ carId, workOrderId });

    return () => {
      surveyStore.reset();
    };
  }, []);

  useEffect(() => {
    const nextQuestion = surveyStore.survey?.questions[fields.length];

    if (!formState.isValid || !nextQuestion) {
      return;
    }

    const preparedNextQuestion = surveyStore.prepareSurveyFormField(nextQuestion);

    append(preparedNextQuestion);
  }, [formState.isValid]);

  useEffect(() => {
    if (fields.length <= 1) {
      return;
    }

    void trigger(SurveyFormField.questions);
  }, [fields]);

  useEffect(() => {
    if (!surveyStore.survey?.questions.length) {
      return;
    }
    // Добавляем первый вопрос в форму
    const preparedValues = surveyStore.prepareSurveyFormDefaultValue(surveyStore.survey.questions);

    // Автозаполнение формы
    reset(preparedValues);
  }, [surveyStore.survey?.questions]);

  // Handlers

  const onSubmit = (form: SurveyForm) => {
    if (!surveyStore.survey) {
      return;
    }

    void surveyStore.sendSurvey(form, surveyStore.survey, { carId, workOrderId }, onSave);
  };

  const handleRepeat = () => {
    surveyStore.getSurvey({ carId, workOrderId });
  };

  // Renders

  return (
    <DataShower loading={surveyStore.surveyLoading} success={surveyStore.surveyLoaded} repeatAction={handleRepeat}>
      <SurveyHeaderText headerText={surveyStore.survey?.headerText} />
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display="flex" flexDirection="column" gap={4}>
            {fields.map((question, parentIndex) => (
              <Box key={question.questionId}>
                <Typography variant="subtitle2" fontSize={16} mb={1}>
                  {`${parentIndex + 1}. ${question.descr ?? ''}`}
                </Typography>

                <QuestionRenderer
                  question={question}
                  name={`${SurveyFormField.questions}[${parentIndex}]`}
                  key={question.questionId}
                />

                {question.extraQuestions?.map((extraQ, extraIndex) => (
                  <ExtraQuestion
                    parentIndex={parentIndex}
                    extraIndex={extraIndex}
                    key={extraQ.questionId}
                    parentName={`${SurveyFormField.questions}[${parentIndex}]`}
                    question={extraQ}
                  />
                ))}
              </Box>
            ))}

            <LoadingButton
              loading={surveyStore.sendSurveyLoading}
              type="submit"
              variant="contained"
              fullWidth
              disabled={!formState.isValid || fields.length !== surveyStore.survey?.questions.length}
            >
              {t('button.submit')}
            </LoadingButton>
          </Box>
        </form>
      </FormProvider>
    </DataShower>
  );
});
